import { animate, useReducedMotion } from "motion/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { LINE_LOADING_PULSE_EASE } from "./line-loading-timing";
import {
  computeSeriesPathPoints,
  interpolateSeriesPathPoints,
  seriesPathFromPoints,
  seriesPathTransitionSignature,
} from "./series-path-utils";

export function useAnimatedSeriesPath({
  renderData,
  xAccessor,
  xScale,
  yScale,
  dataKey,
  curve,
  chartPhase,
  durationMs,
  innerWidth,
  enabled
}) {
  const reducedMotion = useReducedMotion();
  const [animatedPoints, setAnimatedPoints] = useState(null);
  const displayedPointsRef = useRef(null);
  const animatingRef = useRef(false);

  const xScaleDomain = useMemo(() => {
    const scaleWithDomain = xScale;
    return scaleWithDomain.domain?.() ?? [new Date(0), new Date(0)];
  }, [xScale]);

  const transitionSignature = useMemo(() =>
    seriesPathTransitionSignature({
      renderData,
      xAccessor,
      dataKey,
      innerWidth,
      xDomainMin: xScaleDomain[0]?.getTime?.() ?? 0,
      xDomainMax: xScaleDomain[1]?.getTime?.() ?? 0,
    }), [renderData, xAccessor, dataKey, innerWidth, xScaleDomain]);

  const targetPoints = useMemo(() =>
    computeSeriesPathPoints(renderData, xAccessor, xScale, yScale, dataKey), [renderData, xAccessor, xScale, yScale, dataKey]);

  const prevTransitionSignatureRef = useRef(transitionSignature);

  // Lodgeic extension — see LODGEIC.md "Series path animation survives
  // scale changes". The tween reads the LATEST data/scales through this ref
  // instead of closing over them, so a y-domain tween (yScale changes every
  // frame) or a refetch mid-flight no longer cancels the animation. Before,
  // the effect cleanup stopped `animate()` on any dependency change but left
  // the half-morphed frame in `animatedPoints`, and the signature guard then
  // refused to restart — the chart froze on a nonsense path (lines shooting
  // off the plot after a filter change).
  const latestRef = useRef({ renderData, xAccessor, xScale, yScale, dataKey });
  latestRef.current = { renderData, xAccessor, xScale, yScale, dataKey };
  const interruptedRef = useRef(false);

  useEffect(() => {
    if (!animatingRef.current) {
      displayedPointsRef.current = targetPoints;
    }
  }, [targetPoints]);

  useEffect(() => {
    const shouldAnimate =
      enabled &&
      !reducedMotion &&
      chartPhase === "ready" &&
      durationMs > 0 &&
      renderData.length > 0;

    if (!shouldAnimate) {
      animatingRef.current = false;
      interruptedRef.current = false;
      setAnimatedPoints(null);
      displayedPointsRef.current = targetPoints;
      prevTransitionSignatureRef.current = transitionSignature;
      return;
    }

    if (prevTransitionSignatureRef.current === transitionSignature && !interruptedRef.current) {
      return;
    }
    prevTransitionSignatureRef.current = transitionSignature;
    interruptedRef.current = false;

    const fromPoints = displayedPointsRef.current ?? targetPoints;
    if (fromPoints.length === 0) {
      displayedPointsRef.current = targetPoints;
      return;
    }

    animatingRef.current = true;
    const fromSnapshot = fromPoints;
    const currentTargetPoints = () => {
      const l = latestRef.current;
      return computeSeriesPathPoints(l.renderData, l.xAccessor, l.xScale, l.yScale, l.dataKey);
    };

    const control = animate(0, 1, {
      duration: durationMs / 1000,
      ease: [...LINE_LOADING_PULSE_EASE],
      onUpdate: (progress) => {
        const next = interpolateSeriesPathPoints(fromSnapshot, currentTargetPoints(), progress);
        displayedPointsRef.current = next;
        setAnimatedPoints(next);
      },
      onComplete: () => {
        animatingRef.current = false;
        displayedPointsRef.current = currentTargetPoints();
        setAnimatedPoints(null);
      },
    });

    return () => {
      control.stop();
      if (animatingRef.current) {
        // Stopped mid-flight (new data, phase or duration change): the next
        // run must continue from the displayed frame, never park on it.
        interruptedRef.current = true;
      }
      animatingRef.current = false;
    };
    // renderData drives the signature; scales/targetPoints are read live via latestRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitionSignature, chartPhase, durationMs, enabled, reducedMotion, renderData.length]);

  const activePoints = animatedPoints ?? targetPoints;
  const pathD = useMemo(() => seriesPathFromPoints(activePoints, curve), [activePoints, curve]);

  return {
    pathD,
    isPathAnimating: animatedPoints != null,
  };
}
